import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

const StoreIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const MyStoreNotRegistered = ({ refreshUserData, onSignOut }) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('welcome'); // welcome, search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Search locations as user types
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await supabase
          .from('locations')
          .select('id, store_name, city, state, owner_id')
          .ilike('store_name', `%${searchQuery.trim()}%`)
          .limit(10);
        setSearchResults(data || []);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleRegister = async (locationId) => {
    setLoading(true);
    try {
      const updateData = { is_location: true };
      if (locationId) {
        updateData.location_data = { claimed_location_id: locationId };
      }

      const { error } = await supabase
        .from('users')
        .update(updateData)
        .eq('auth_id', user.id);

      if (error) throw error;
      refreshUserData();
    } catch (err) {
      console.error('Register error:', err);
      alert('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    card: {
      width: '100%',
      maxWidth: 400,
      textAlign: 'center',
    },
    iconCircle: {
      width: 80,
      height: 80,
      borderRadius: '50%',
      backgroundColor: 'rgba(14, 165, 233, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      color: '#0ea5e9',
    },
    title: {
      fontSize: 20,
      fontWeight: 600,
      color: theme.text,
      margin: '0 0 8px',
    },
    text: {
      fontSize: 14,
      color: theme.textSecondary,
      lineHeight: 1.5,
      margin: '0 0 24px',
    },
    button: {
      width: '100%',
      padding: 14,
      borderRadius: 12,
      border: 'none',
      backgroundColor: theme.accent,
      color: '#fff',
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      marginBottom: 12,
    },
    signOutBtn: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: 'transparent',
      color: theme.textSecondary,
      fontSize: 14,
      cursor: 'pointer',
    },
    searchInput: {
      width: '100%',
      padding: '14px 14px 14px 42px',
      borderRadius: 12,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgCard,
      color: theme.text,
      fontSize: 15,
      outline: 'none',
      boxSizing: 'border-box',
    },
    searchWrapper: {
      position: 'relative',
      marginBottom: 16,
      textAlign: 'left',
    },
    searchIconWrapper: {
      position: 'absolute',
      left: 14,
      top: '50%',
      transform: 'translateY(-50%)',
      color: theme.textMuted,
      pointerEvents: 'none',
    },
    resultsList: {
      textAlign: 'left',
      marginBottom: 16,
    },
    resultItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 14px',
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgCard,
      cursor: 'pointer',
      marginBottom: 8,
      width: '100%',
      textAlign: 'left',
    },
    resultItemSelected: {
      border: `2px solid ${theme.accent}`,
      backgroundColor: 'rgba(14, 165, 233, 0.05)',
    },
    resultName: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    resultLocation: {
      fontSize: 12,
      color: theme.textMuted,
      margin: 0,
    },
    resultOwned: {
      fontSize: 11,
      color: '#f59e0b',
      fontWeight: 500,
    },
    backLink: {
      background: 'none',
      border: 'none',
      color: theme.accent,
      fontSize: 13,
      cursor: 'pointer',
      marginBottom: 20,
      padding: 0,
    },
    notListedBtn: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px dashed ${theme.border}`,
      backgroundColor: 'transparent',
      color: theme.textSecondary,
      fontSize: 13,
      cursor: 'pointer',
      marginBottom: 12,
    },
  };

  // Step 1: Welcome
  if (step === 'welcome') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.iconCircle}>
            <StoreIcon />
          </div>
          <h2 style={styles.title}>Welcome to CardChase</h2>
          <p style={styles.text}>
            Already have a location on the app you need to claim? Or register a new store as a CardChase Location Partner.
          </p>
          <button
            style={styles.button}
            onClick={() => setStep('search')}
          >
            Find My Store
          </button>
          <button
            style={{ ...styles.button, backgroundColor: 'transparent', color: theme.text, border: `1px solid ${theme.border}` }}
            onClick={() => handleRegister(null)}
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register New Store'}
          </button>
          <button style={styles.signOutBtn} onClick={onSignOut}>
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Search for your store
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <button style={styles.backLink} onClick={() => { setStep('welcome'); setSearchQuery(''); setSelectedLocation(null); }}>
          Back
        </button>
        <h2 style={{ ...styles.title, textAlign: 'left' }}>Find Your Store</h2>
        <p style={{ ...styles.text, textAlign: 'left', marginBottom: 16 }}>
          Search for your store below. If it's already listed, you can claim it.
        </p>

        <div style={styles.searchWrapper}>
          <div style={styles.searchIconWrapper}><SearchIcon /></div>
          <input
            type="text"
            placeholder="Type your store name..."
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setSelectedLocation(null); }}
            style={styles.searchInput}
            autoFocus
          />
        </div>

        {searching && (
          <p style={{ fontSize: 13, color: theme.textMuted, textAlign: 'left' }}>Searching...</p>
        )}

        {searchResults.length > 0 && (
          <div style={styles.resultsList}>
            {searchResults.map(loc => {
              const isOwned = !!loc.owner_id;
              const isSelected = selectedLocation?.id === loc.id;
              return (
                <button
                  key={loc.id}
                  style={{
                    ...styles.resultItem,
                    ...(isSelected ? styles.resultItemSelected : {}),
                    ...(isOwned ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  onClick={() => !isOwned && setSelectedLocation(loc)}
                  disabled={isOwned}
                >
                  <MapPinIcon />
                  <div>
                    <p style={styles.resultName}>{loc.store_name}</p>
                    <p style={styles.resultLocation}>
                      {[loc.city, loc.state].filter(Boolean).join(', ')}
                    </p>
                    {isOwned && <span style={styles.resultOwned}>Already claimed</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {searchQuery.trim().length >= 2 && !searching && searchResults.length === 0 && (
          <p style={{ fontSize: 13, color: theme.textMuted, textAlign: 'left', marginBottom: 16 }}>
            No stores found matching "{searchQuery}"
          </p>
        )}

        {selectedLocation && (
          <button
            style={{ ...styles.button, opacity: loading ? 0.6 : 1 }}
            onClick={() => handleRegister(selectedLocation.id)}
            disabled={loading}
          >
            {loading ? 'Claiming...' : `Claim "${selectedLocation.store_name}"`}
          </button>
        )}

        <button
          style={styles.notListedBtn}
          onClick={() => handleRegister(null)}
          disabled={loading}
        >
          My store isn't listed. Register as new.
        </button>

        <button style={styles.signOutBtn} onClick={onSignOut}>
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default MyStoreNotRegistered;
